import React, { useState, useContext, useEffect } from "react";
import { motion } from "framer-motion";
import "./Login.css";
import logo from "../../assets/Logo.webp";
import logoText from "../../assets/logo-text-only.webp";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import AuthContext from "../../AuthContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { parseReturnTo } from "../../utils/returnTo";

// Para desarrollo con el nuevo Auth Service
import AuthService from "../../services/auth.service.js";

const currentEnv = process.env.REACT_APP_ENV;
const isLoginDisabled = currentEnv === "prod" || currentEnv === "test";

export default function Login() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { authenticateUser, isAuthenticated, loginWithJWT, isUsingJWT } = useContext(AuthContext);

  // REDIRECCIÓN SI YA ESTÁ AUTENTICADO — Home es la landing, no Zeno ("/").
  useEffect(() => {
    if (isAuthenticated) {
      navigate(parseReturnTo(location.search, "/home"), { replace: true });
    }
  }, [isAuthenticated, navigate, location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError("Please enter email and password");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      if (loginWithJWT) {
        const result = await loginWithJWT(formData.email, formData.password);
        
        if (result && result.user && result.user.user_id) {
          sessionStorage.setItem('showLoginAnimation', 'true');
          navigate(parseReturnTo(location.search, "/home"));
          return;
        } else {
          setError("Login failed. Please try again.");
          setIsLoading(false);
        }
      }
    } catch (error) {
      setError(error.message || "Login failed. Please check your credentials.");
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      {isLoading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(168, 5, 255, 0.2)',
            borderTop: '4px solid #a805ff',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
      
      <div className="brand-container">
        <div className="logo-wrapper">
          <img src={logo} alt="Zenit AI Logo" className="logo" />
          <img src={logoText} alt="Zenit AI" className="logo-text" />
        </div>
      </div>

      <div className="login-box">
        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          <div className="input-container">
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
              required
            />
          </div>

          <div className="input-container password-container">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              disabled={isLoading}
              required
            />
            <span className="password-icon" onClick={toggleShowPassword}>
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>

          <div className="form-options">
            <label className="remember-me">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={handleRememberMeChange}
              />
              <span>Remember me</span>
            </label>
            <NavLink to="/ForgotPassword" className="forgot-password">
              Forgot Password?
            </NavLink>
          </div>

          <button type="submit" className="login-btn" disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Login'}
          </button>
        </form>

        <div className="signup-link">
          Don't have an account? <NavLink to="/SignUp">Sign Up</NavLink>
        </div>
      </div>
    </div>
  );
}
